import React, { useContext, useState, useEffect, useCallback, lazy, Suspense } from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate, useSearchParams, Link } from 'react-router-dom';
import AuthContext from './context/AuthContext';
import './App.css';
import { selectOrCreateConversation } from './utils/chatApi';
import heroBackground from './assets/heros/hero-background.webp';

// ⚡ CRITICAL COMPONENTS - Load immediately
import Navbar from './components/Navbar';
import Homepage from './components/Homepage';
import Footer from './components/Footer';
import ModalPortal from './components/ModalPortal';
import GlobalChatController from './components/GlobalChatController';

// ⚡ LAZY LOADED COMPONENTS - Load on demand (Code Splitting)
const Games = lazy(() => import('./components/Games'));
const GameDetail = lazy(() => import('./components/games/GameDetail'));
const Tournaments = lazy(() => import('./components/Tournaments'));
const NewTournamentDetail = lazy(() => import('./components/tournaments/NewTournamentDetail'));
const TournamentModalVersus = lazy(() => import('./components/tournaments/TournamentModalVersus'));
const Shop = lazy(() => import('./components/Shop'));
const Login = lazy(() => import('./components/Login'));
const Register = lazy(() => import('./components/Register'));
const ForgotPassword = lazy(() => import('./components/ForgotPassword'));
const ResetPassword = lazy(() => import('./components/ResetPassword'));
const EmailVerificationPage = lazy(() => import('./components/EmailVerificationPage'));
const AccountDeletionConfirmation = lazy(() => import('./components/AccountDeletionConfirmation'));
const NewsletterUnsubscribe = lazy(() => import('./components/NewsletterUnsubscribe'));
const UserDashboard = lazy(() => import('./components/UserDashboard'));
const AdminDashboard = lazy(() => import('./components/AdminDashboard'));
const AdminPanel = lazy(() => import('./components/AdminPanel'));
const AdminLogin = lazy(() => import('./components/AdminLogin'));
const PublicProfile = lazy(() => import('./components/PublicProfile'));
const VersusToken = lazy(() => import('./components/VersusToken'));
const Chat = lazy(() => import('./components/Chat'));
const DownloadsHub = lazy(() => import('./components/DownloadsHub'));
const Imprint = lazy(() => import('./components/Imprint'));
const Privacy = lazy(() => import('./components/Privacy'));
const Terms = lazy(() => import('./components/Terms'));
const FAQ = lazy(() => import('./components/FAQ'));
const Newsletter = lazy(() => import('./components/Newsletter'));
const CompleteProfile = lazy(() => import('./components/CompleteProfile'));
const EarlyAccessPayment = lazy(() => import('./components/earlyaccess/EarlyAccessPayment'));
const EarlyAccessSuccess = lazy(() => import('./components/earlyaccess/EarlyAccessSuccess'));

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

// Cookie Banner Component
const CookieBanner = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [isAnimatingOut, setIsAnimatingOut] = useState(false);

  useEffect(() => {
    // Check if user has already given consent
    const cookieConsent = localStorage.getItem('cookieConsent');
    if (!cookieConsent) {
      // Show banner after a short delay for better UX
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAccept = () => {
    // Store consent with timestamp
    const consent = {
      accepted: true,
      timestamp: new Date().toISOString(),
      version: '1.0' // For future cookie policy updates
    };
    localStorage.setItem('cookieConsent', JSON.stringify(consent));
    
    // Animate out before hiding
    setIsAnimatingOut(true);
    setTimeout(() => {
      setIsVisible(false);
    }, 300);
  };

  const handleDecline = () => {
    // Store decline decision (optional: you might want to limit functionality)
    const consent = {
      accepted: false,
      timestamp: new Date().toISOString(),
      version: '1.0'
    };
    localStorage.setItem('cookieConsent', JSON.stringify(consent));
    
    // Animate out before hiding
    setIsAnimatingOut(true);
    setTimeout(() => {
      setIsVisible(false);
    }, 300);
  };

  if (!isVisible) return null;

  return (
    <div 
      className={`cookie-banner ${isAnimatingOut ? 'cookie-banner--hiding' : ''}`}
      style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        background: 'linear-gradient(135deg, #1a1a2e, #16213e)',
        color: 'white',
        padding: window.innerWidth <= 768 ? '16px' : '20px',
        boxShadow: '0 -4px 20px rgba(0, 0, 0, 0.3)',
        borderTop: '1px solid rgba(255, 255, 255, 0.1)',
        zIndex: 1000,
        transform: isAnimatingOut ? 'translateY(100%)' : 'translateY(0)',
        transition: 'transform 0.3s ease-in-out',
        opacity: isAnimatingOut ? 0 : 1,
        paddingBottom: `calc(${window.innerWidth <= 768 ? '16px' : '20px'} + env(safe-area-inset-bottom))`
      }}
    >
      <div style={{
        maxWidth: '1200px',
        margin: '0 auto',
        display: 'flex',
        alignItems: window.innerWidth <= 768 ? 'flex-start' : 'center',
        justifyContent: 'space-between',
        flexDirection: window.innerWidth <= 768 ? 'column' : 'row',
        gap: window.innerWidth <= 768 ? '16px' : '20px'
      }}>
        <div style={{ 
          display: 'flex', 
          alignItems: window.innerWidth <= 480 ? 'flex-start' : 'center', 
          gap: window.innerWidth <= 480 ? '12px' : '15px', 
          flex: 1,
          flexDirection: window.innerWidth <= 480 ? 'column' : 'row',
          textAlign: window.innerWidth <= 480 ? 'center' : 'left'
        }}>
          <div style={{ fontSize: window.innerWidth <= 480 ? '1.5em' : '2em' }}>🍪</div>
          <div style={{ flex: 1 }}>
            <h3 style={{ 
              margin: '0 0 5px 0', 
              color: '#00d4aa', 
              fontSize: window.innerWidth <= 480 ? '1em' : '1.1em',
              textAlign: window.innerWidth <= 480 ? 'center' : 'left'
            }}>
              We use cookies to enhance your experience
            </h3>
            <p style={{ 
              margin: 0, 
              fontSize: window.innerWidth <= 480 ? '0.8em' : '0.9em', 
              color: '#d1d5db',
              lineHeight: '1.4',
              textAlign: window.innerWidth <= 480 ? 'center' : 'left'
            }}>
              {window.innerWidth <= 480 ? 
                'We use cookies to improve your experience. By clicking "Accept", you consent to our use of cookies.' :
                'We use essential cookies to make our website work and analytics cookies to understand how you interact with our website. By clicking "Accept All", you consent to our use of cookies.'
              }
            </p>
          </div>
        </div>
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: window.innerWidth <= 768 ? '12px' : '15px', 
          flexWrap: 'wrap',
          flexDirection: window.innerWidth <= 480 ? 'column' : 'row',
          width: window.innerWidth <= 768 ? '100%' : 'auto'
        }}>
          <div style={{ 
            display: 'flex', 
            gap: window.innerWidth <= 480 ? '6px' : '10px',
            justifyContent: window.innerWidth <= 480 ? 'center' : 'flex-start',
            flexWrap: 'wrap'
          }}>
            <Link 
              to="/privacy" 
              style={{ 
                color: '#00d4aa', 
                textDecoration: 'none', 
                fontSize: window.innerWidth <= 480 ? '0.8em' : '0.9em',
                whiteSpace: 'nowrap'
              }}
            >
              Privacy Policy
            </Link>
            <Link 
              to="/terms" 
              style={{ 
                color: '#00d4aa', 
                textDecoration: 'none', 
                fontSize: window.innerWidth <= 480 ? '0.8em' : '0.9em',
                whiteSpace: 'nowrap'
              }}
            >
              Cookie Settings
            </Link>
          </div>
          <div style={{ 
            display: 'flex', 
            gap: window.innerWidth <= 480 ? '8px' : '10px',
            width: window.innerWidth <= 480 ? '100%' : 'auto',
            justifyContent: window.innerWidth <= 480 ? 'center' : 'flex-start'
          }}>
            <button 
              onClick={handleDecline}
              style={{
                background: 'transparent',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                color: 'white',
                padding: window.innerWidth <= 480 ? '10px 16px' : '8px 16px',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: window.innerWidth <= 480 ? '0.85em' : '0.9em',
                transition: 'all 0.2s ease',
                minHeight: window.innerWidth <= 480 ? '44px' : 'auto',
                flex: window.innerWidth <= 480 ? '1' : 'none',
                touchAction: 'manipulation'
              }}
            >
              Decline
            </button>
            <button 
              onClick={handleAccept}
              style={{
                background: 'linear-gradient(135deg, #00d4aa, #22c55e)',
                border: 'none',
                color: 'white',
                padding: window.innerWidth <= 480 ? '10px 20px' : '8px 20px',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: window.innerWidth <= 480 ? '0.85em' : '0.9em',
                fontWeight: '600',
                transition: 'all 0.2s ease',
                minHeight: window.innerWidth <= 480 ? '44px' : 'auto',
                flex: window.innerWidth <= 480 ? '1' : 'none',
                touchAction: 'manipulation'
              }}
            >
              {window.innerWidth <= 480 ? 'Accept' : 'Accept All'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
// Temporary FAQ Component
  const ConfirmDeletion = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [status, setStatus] = useState('loading');
    const [message, setMessage] = useState('');
    const [isProcessing, setIsProcessing] = useState(false);

    const token = searchParams.get('token');
    const userId = searchParams.get('userId');

    const confirmDeletion = useCallback(async () => {
      if (isProcessing) return;
      
      setIsProcessing(true);
      
      try {
        const res = await fetch(`${API_URL}/api/user/confirm-deletion`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token, userId }),
        });

        const data = await res.json();

        if (res.ok) {
          setStatus('success');
          setMessage('Your account has been successfully deleted.');
          localStorage.clear();
          setTimeout(() => {
            window.location.href = '/';
          }, 3000);
        } else {
          setStatus('error');
          setMessage(data.msg || 'Account deletion failed.');
        }
      } catch (e) {
        console.error(e);
        setStatus('error');
        setMessage('Network error. Please try again or contact support.');
      } finally {
        setIsProcessing(false);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isProcessing, token, userId]);

    useEffect(() => {
      if (!token || !userId) {
        setStatus('error');
        setMessage('Invalid deletion link. Please request a new account deletion from your settings.');
        return;
      }

      const timer = setTimeout(() => {
        confirmDeletion();
      }, 10000);

      return () => clearTimeout(timer);
    }, [token, userId, confirmDeletion]);

    const cancelDeletion = () => {
      navigate('/');
    };

    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #0c0f1a, #1a1a2e)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px'
      }}>
        <div style={{
          maxWidth: '500px',
          width: '100%',
          background: 'rgba(15, 18, 26, 0.9)',
          borderRadius: '16px',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          padding: '40px',
          textAlign: 'center',
          boxShadow: '0 20px 40px rgba(0, 0, 0, 0.5)'
        }}>
          {status === 'loading' && (
            <>
              <div style={{fontSize: '4em', marginBottom: '20px'}}>⚠️</div>
              <h1 style={{
                fontSize: '2em',
                fontWeight: '700',
                marginBottom: '16px',
                background: 'linear-gradient(135deg, #f59e0b, #e94560)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}>Confirm Account Deletion</h1>
              <p style={{fontSize: '1.1em', color: '#d1d5db', lineHeight: '1.6', marginBottom: '30px'}}>
                You are about to permanently delete your Versus Arena account.
              </p>
              
              <div style={{
                background: 'rgba(239, 68, 68, 0.1)',
                border: '1px solid rgba(239, 68, 68, 0.3)',
                borderRadius: '8px',
                padding: '16px',
                marginBottom: '24px',
                color: '#fca5a5'
              }}>
                <strong>⚠️ This action cannot be undone!</strong><br/>
                All your data will be permanently deleted.
              </div>

              <div style={{
                background: 'rgba(245, 158, 11, 0.1)',
                border: '1px solid rgba(245, 158, 11, 0.3)',
                borderRadius: '8px',
                padding: '16px',
                marginBottom: '24px',
                color: '#fbbf24'
              }}>
                <strong>⏱️ Auto-deletion in 10 seconds</strong><br/>
                Click "Cancel" if you want to keep your account.
              </div>

              <div style={{display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap'}}>
                <button 
                  style={{
                    padding: '12px 24px',
                    borderRadius: '8px',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    background: 'rgba(255, 255, 255, 0.1)',
                    color: '#d1d5db',
                    fontWeight: '600',
                    cursor: 'pointer',
                    minWidth: '120px'
                  }}
                  onClick={cancelDeletion}
                >
                  Cancel
                </button>
                <button 
                  style={{
                    padding: '12px 24px',
                    borderRadius: '8px',
                    border: 'none',
                    background: 'linear-gradient(135deg, #ef4444, #dc2626)',
                    color: 'white',
                    fontWeight: '600',
                    cursor: 'pointer',
                    minWidth: '120px'
                  }}
                  onClick={confirmDeletion}
                  disabled={isProcessing}
                >
                  {isProcessing ? 'Deleting...' : 'Delete Now'}
                </button>
              </div>
            </>
          )}

          {status === 'success' && (
            <>
              <div style={{fontSize: '4em', marginBottom: '20px'}}>✅</div>
              <h1 style={{
                fontSize: '2em',
                fontWeight: '700',
                marginBottom: '16px',
                background: 'linear-gradient(135deg, #22c55e, #00d4aa)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}>Account Deleted</h1>
              <p style={{fontSize: '1.1em', color: '#d1d5db', lineHeight: '1.6', marginBottom: '30px'}}>
                Your account has been successfully deleted. You will be redirected shortly.
              </p>
            </>
          )}

          {status === 'error' && (
            <>
              <div style={{fontSize: '4em', marginBottom: '20px'}}>❌</div>
              <h1 style={{
                fontSize: '2em',
                fontWeight: '700',
                marginBottom: '16px',
                background: 'linear-gradient(135deg, #ef4444, #e94560)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}>Deletion Failed</h1>
              <p style={{fontSize: '1.1em', color: '#d1d5db', lineHeight: '1.6', marginBottom: '30px'}}>
                {message}
              </p>
              <button 
                style={{
                  padding: '12px 24px',
                  borderRadius: '8px',
                  border: 'none',
                  background: 'linear-gradient(135deg, #00d4aa, #22c55e)',
                  color: 'white',
                  fontWeight: '600',
                  cursor: 'pointer',
                  minWidth: '120px'
                }}
                onClick={() => window.location.href = '/'}
              >
                Go to Homepage
              </button>
            </>
          )}
        </div>
      </div>
    );
  };

  const OAuthSuccess = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { setUser } = useContext(AuthContext);

    useEffect(() => {
      const token = searchParams.get('token');
      const error = searchParams.get('error');
      const needsCompletion = searchParams.get('complete') === 'true';

      console.log('🔍 Frontend OAuth Debug:');
      console.log('🎫 Token:', !!token);
      console.log('❌ Error:', error);
      console.log('✅ Needs completion:', needsCompletion);

      if (error) {
        alert('OAuth login failed: ' + error);
        navigate('/login');
        return;
      }

      if (token) {
        // Store token and get user data
        localStorage.setItem('token', token);
        
        // Fetch user data
        fetch(`${API_URL}/api/user`, {
          headers: { Authorization: `Bearer ${token}` }
        })
        .then(res => res.json())
        .then(userData => {
          console.log('👤 User data received:', userData);
          console.log('📋 Profile completed:', userData?.profileCompleted);
          
          if (userData) {
            setUser(userData);
            localStorage.setItem('user', JSON.stringify(userData));
            
            // Check if profile needs completion
            if (needsCompletion || !userData.profileCompleted) {
              console.log('🚀 Navigating to complete-profile');
              navigate('/complete-profile');
            } else {
              console.log('🚀 Navigating to account');
              navigate('/account');
            }
          } else {
            navigate('/login');
          }
        })
        .catch((err) => {
          console.error('❌ Fetch error:', err);
          navigate('/login');
        });
      } else {
        navigate('/login');
      }
    }, [searchParams, navigate, setUser]);

    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #0c0f1a, #1a1a2e)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'white'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '3em', marginBottom: '20px' }}>🔄</div>
          <h2>Completing login...</h2>
          <p>Please wait while we set up your account.</p>
        </div>
      </div>
    );
  };

// Private Route: benötigt Login
 function PrivateRoute({ children, roles }) {
  const ctx = useContext(AuthContext) || {};
  const { user, isLoading } = ctx;
  
  // DEBUG für Backoffice-Problem
  console.log('🔍 PrivateRoute Debug:');
  console.log('- isLoading:', isLoading);
  console.log('- user:', user);
  console.log('- user.role:', user?.role);
  console.log('- user.isAdmin:', user?.isAdmin);
  console.log('- required roles:', roles);
  
  if (isLoading) {
    console.log('⏳ Loading...');
    return <div style={{padding: '20px', textAlign: 'center'}}>Loading...</div>;
  }
  
  if (!user) {
    console.log('❌ No user - redirecting to backoffice login');
    // Für Backoffice-Routen, zu spezieller Login-Seite weiterleiten
    const isBackofficeRoute = window.location.pathname.includes('/versusbackoffice');
    if (isBackofficeRoute) {
      return <Navigate to="/backoffice-login" replace />;
    }
    return <Navigate to="/login" replace />;
  }
  
  // Check role-based access
  if (roles && roles.length > 0) {
    const hasAccess = roles.some(role => {
      if (role === 'admin') {
        return user.isAdmin || user.role === 'admin';
      }
      return user.role === role;
    });
    
    console.log('🔐 Access check:', hasAccess);
    
    if (!hasAccess) {
      console.log('❌ Access denied. User role:', user.role, 'Required roles:', roles, 'isAdmin:', user.isAdmin);
      alert(`❌ Zugriff verweigert!\n\nIhre Rolle: ${user.role}\nBenötigt: ${roles.join(' oder ')}\nAdmin: ${user.isAdmin}\n\nBitte als Admin/Master anmelden.`);
      return <Navigate to="/" replace />;
    }
  }
  
  console.log('✅ Access granted');
  return children;
 }

// Chat wird jetzt state-based gerendert (siehe unten im return-Statement)

function App() {
  const ctx = useContext(AuthContext) || {};
  const { user } = ctx;
  const location = useLocation();
  const background = location.state && location.state.background;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // State-based Chat Management (no URL routing)
  const [openChatId, setOpenChatId] = useState(null);
 
  // Einheitlicher Einstieg: öffnet Chat via State (KEINE URL-Änderung)
  // Akzeptiert entweder eine chatId (string) oder { _id, chatId } eines Users
  const openChat = async (input) => {
    let chatId = typeof input === 'string' ? input : input?.chatId;
    if (!chatId && input && input._id) {
      chatId = await selectOrCreateConversation(input._id);
    }
    if (chatId) {
      setOpenChatId(chatId); // Open via state - no URL change
    }
  };
  
  const closeChat = () => {
    setOpenChatId(null);
  };

  // Handle ?openChat=chatId from email links (only entry point via URL)
  useEffect(() => {
    const chatIdFromUrl = searchParams.get('openChat');
    if (chatIdFromUrl && user) {
      // Remove query param
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete('openChat');
      navigate({
        pathname: location.pathname,
        search: newSearchParams.toString()
      }, { replace: true });
      
      // Open the chat (async is okay here)
      openChat(chatIdFromUrl).catch(err => {
        console.error('Failed to open chat from email link:', err);
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams.get('openChat'), user]);

  return (
    <div className="App">
      <Navbar />

      {/* ⚡ SUSPENSE WRAPPER - Shows loading state while lazy components load */}
      <Suspense fallback={
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '60vh',
          flexDirection: 'column',
          gap: '1rem'
        }}>
          <div className="spinner" style={{
            border: '4px solid rgba(255, 140, 0, 0.1)',
            borderTop: '4px solid #ff8c00',
            borderRadius: '50%',
            width: '50px',
            height: '50px',
            animation: 'spin 1s linear infinite'
          }}></div>
          <p style={{ color: '#fff', fontSize: '1.1rem' }}>Loading...</p>
        </div>
      }>
        {/* Basis-Routen: rendern mit background oder aktueller Location */}
        <Routes location={background || location}>
        <Route path="/" element={<Homepage />} />
        <Route path="/tournaments" element={<Tournaments />} />
        <Route path="/tournaments/:tournamentId" element={<TournamentModalVersus />} />
        <Route path="/tournaments/old/:tournamentId" element={<NewTournamentDetail />} />
        <Route path="/shop" element={<Shop />} />
        <Route path="/versus-token" element={<VersusToken />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/verify-email/:token" element={<EmailVerificationPage />} />
        <Route path="/confirm-deletion/:token" element={<AccountDeletionConfirmation />} />
        <Route path="/newsletter/unsubscribe" element={<NewsletterUnsubscribe />} />
        <Route path="/profile/:username" element={<PublicProfile />} />
        <Route path="/games" element={<Games />} />
        <Route path="/games/:slug" element={<GameDetail />} />
        <Route path="/downloads" element={<DownloadsHub />} />
        
        {/* Legal & FAQ Routen */}
        <Route path="/imprint" element={<Imprint />} />
        <Route path="/impressum" element={<Imprint />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/privacy-policy" element={<Privacy />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/faq" element={<FAQ />} />
        <Route path="/newsletter" element={<Newsletter />} />
        
        {/* Protected Routes */}
        <Route
          path="/account"
          element={
            <PrivateRoute>
              <UserDashboard openChat={openChat} />
            </PrivateRoute>
          }
        />
        
        {/* Early Access - Admin Only */}
        <Route
          path="/early-access"
          element={
            <PrivateRoute roles={['admin', 'master']}>
              <EarlyAccessPayment />
            </PrivateRoute>
          }
        />
        <Route
          path="/early-access/success"
          element={
            <PrivateRoute roles={['admin', 'master']}>
              <EarlyAccessSuccess />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin-dashboard"
          element={
            <PrivateRoute roles={['admin']}>
              <AdminDashboard />
            </PrivateRoute>
          }
        />
        
        {/* ADMIN LOGIN - Sichere Anmeldung */}
        <Route path="/admin-login" element={<AdminLogin />} />
        
        {/* NEUES ADMIN PANEL - NUR für authentifizierte Admins */}
        <Route
          path="/admin"
          element={
            <PrivateRoute roles={['admin', 'master']}>
              <AdminPanel />
            </PrivateRoute>
          }
        />
        <Route
          path="/versusbackoffice"
          element={
            <PrivateRoute roles={['admin', 'master']}>
              <AdminPanel />
            </PrivateRoute>
          }
        />
        <Route
          path="/versusbackoffice/*"
          element={
            <PrivateRoute roles={['admin', 'master']}>
              <AdminPanel />
            </PrivateRoute>
          }
        />
        
        {/* Account Deletion Confirmation */}
        <Route path="/confirm-deletion" element={<ConfirmDeletion />} />
        
        {/* OAuth Success Handler */}
        <Route path="/oauth-success" element={<OAuthSuccess />} />
        
        {/* Complete Profile for OAuth users */}
        <Route path="/complete-profile" element={<CompleteProfile />} />

        {/* 404 Fallback - must be last */}
        <Route path="*" element={
          <div style={{padding: '120px 20px 80px', textAlign: 'center', color: 'white', maxWidth: '800px', margin: '0 auto'}}>
            <div style={{fontSize: '4em', marginBottom: '20px'}}>404</div>
            <h1 style={{fontSize: '2.5em', marginBottom: '20px', color: '#e94560'}}>Page Not Found</h1>
            <p style={{fontSize: '1.2em', marginBottom: '30px', color: '#9ca3af'}}>
              The requested page could not be found. It may have been moved, renamed, or no longer exists.
            </p>
            <div style={{display: 'flex', gap: '15px', justifyContent: 'center', flexWrap: 'wrap', marginBottom: '40px'}}>
              <a href="/" style={{
                background: 'linear-gradient(135deg, #e94560, #00d4aa)',
                color: 'white',
                padding: '12px 24px',
                borderRadius: '8px',
                textDecoration: 'none',
                fontWeight: '600'
              }}>
                🏠 Back to Home
              </a>
              <a href="/games" style={{
                border: '1px solid #00d4aa',
                color: '#00d4aa',
                padding: '12px 24px',
                borderRadius: '8px',
                textDecoration: 'none'
              }}>
                🎮 Browse Games
              </a>
            </div>
            <div style={{backgroundColor: '#1a1a2e', padding: '20px', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.1)'}}>
              <h3 style={{color: '#00d4aa', marginBottom: '15px'}}>Popular sections:</h3>
              <div style={{display: 'flex', gap: '10px', justifyContent: 'center', flexWrap: 'wrap'}}>
                <a href="/tournaments" style={{color: '#e94560', textDecoration: 'none'}}>🏆 Tournaments</a>
                <a href="/shop" style={{color: '#e94560', textDecoration: 'none'}}>🛒 Shop</a>
                <a href="/versus-token" style={{color: '#e94560', textDecoration: 'none'}}>🪙 Versus Token</a>
                <a href="/faq" style={{color: '#e94560', textDecoration: 'none'}}>❓ FAQ</a>
              </div>
            </div>
          </div>
        } />
      </Routes>
      </Suspense>

       <Footer />

      {/* Controller einfach immer rendern; die Body-Klasse 'chat-open' blendet ihn beim Overlay aus */}
      {user && (
        <GlobalChatController openChat={openChat} />
      )}
      
      {/* State-based Chat Modal - no URL routing */}
      {user && openChatId && (
        <ModalPortal>
          <Chat conversationId={openChatId} onClose={closeChat} />
        </ModalPortal>
      )}

      {/* Cookie Banner - always render but only shows when needed */}
      <CookieBanner />
    </div>
   );
 }
 
 export default App;